/**
 * Sparren und Pfetten (Wunsch von Mathias, 11/2026).
 *
 * Das Luftbild zeigt die Dachhaut, nicht das Tragwerk. Wo die Sparren liegen,
 * weiß nur, wer am Dach war — und genau dort sitzen die Dachhaken. Deshalb
 * werden Abstand und Lage des ersten Trägers eingetragen, die Linien auf das
 * Dach gelegt, und die Haken auf die Träger gesetzt, die unter einer Schiene
 * durchlaufen, statt pauschal nach einem Abstand gezählt.
 *
 * Alles in der Dachebene: x längs der Traufe, vom Boden aus gesehen nach
 * rechts, y von der Traufe zum First. Sparren laufen von der Traufe zum First,
 * liegen also bei festem x; Pfetten laufen längs der Traufe, bei festem y.
 */
import { huellkasten } from './polygon.js';
/** Ein Träger mehr als 2000 je Dach ist ein Tippfehler, kein Dach. */
const HOECHSTENS = 2000;
/** Lagen ab `von + erster` im Abstand, bis `bis`. */
export function traegerlagen(von, bis, abstandM, ersterM = 0) {
    if (!(abstandM > 0.05))
        return [];
    const lagen = [];
    for (let p = von + Math.max(0, ersterM); p <= bis + 1e-9 && lagen.length < HOECHSTENS; p += abstandM) {
        lagen.push(p);
    }
    return lagen;
}
/**
 * Sparren und Pfetten eines Dachs, als Lagen und als beschnittene Linien.
 * Ohne eingetragenen Abstand bleibt die jeweilige Liste leer.
 */
export function traegerlinien(dach, tragwerk) {
    const k = huellkasten(dach);
    const sparrenX = tragwerk.sparrenabstandM
        ? traegerlagen(k.minX, k.maxX, tragwerk.sparrenabstandM, tragwerk.ersterSparrenM ?? 0)
        : [];
    const pfettenY = tragwerk.pfettenabstandM
        ? traegerlagen(k.minY, k.maxY, tragwerk.pfettenabstandM, tragwerk.erstePfetteM ?? 0)
        : [];
    return {
        sparrenX,
        pfettenY,
        sparren: sparrenX.flatMap((x) => abschnitte(dach, x, 'senkrecht').map(([a, b]) => [
            { x, y: a },
            { x, y: b },
        ])),
        pfetten: pfettenY.flatMap((y) => abschnitte(dach, y, 'waagrecht').map(([a, b]) => [
            { x: a, y },
            { x: b, y },
        ])),
    };
}
/**
 * Wo schneidet die Linie x = c (senkrecht) oder y = c (waagrecht) das Dach?
 * Paarweise Schnittpunkte ergeben die Stücke innerhalb — bei einer Gaube
 * oder einem L-förmigen Dach mehr als eines.
 */
function abschnitte(ring, c, richtung) {
    const quer = (p) => (richtung === 'senkrecht' ? p.x : p.y);
    const laengs = (p) => (richtung === 'senkrecht' ? p.y : p.x);
    const schnitte = [];
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const qa = quer(a);
        const qb = quer(b);
        // Halboffen, damit eine Ecke genau auf der Linie nicht doppelt zählt.
        if ((qa <= c && qb > c) || (qb <= c && qa > c)) {
            const t = (c - qa) / (qb - qa);
            schnitte.push(laengs(a) + t * (laengs(b) - laengs(a)));
        }
    }
    schnitte.sort((p, q) => p - q);
    const stuecke = [];
    for (let i = 0; i + 1 < schnitte.length; i += 2)
        stuecke.push([schnitte[i], schnitte[i + 1]]);
    return stuecke;
}
/**
 * Wie viele Haken braucht eine Schiene von `von` bis `bis`, wenn sie nur auf
 * Trägern sitzen dürfen?
 *
 * Der erste und der letzte Träger unter der Schiene bekommen immer einen;
 * dazwischen wird jeweils der am weitesten entfernte genommen, der noch
 * innerhalb des größten Hakenabstands liegt. Bei 60 cm Sparrenabstand und
 * höchstens 1,20 m reicht jeder zweite Sparren; bei 80 cm braucht es jeden,
 * denn jeder zweite wären 1,60 m. Liegen zwei Träger weiter auseinander als der
 * Abstand erlaubt, kommt trotzdem nur auf Träger ein Haken: dazwischen ist
 * nichts, woran er hielte.
 *
 * Läuft kein Träger unter der Schiene durch, gibt es null zurück — dann zählt
 * wieder der eingestellte Abstand.
 */
export function hakenAufTraegern(von, bis, traeger, hoechstabstandM) {
    const unter = traeger.filter((t) => t >= von - 1e-9 && t <= bis + 1e-9).sort((a, b) => a - b);
    if (unter.length === 0)
        return null;
    if (!(hoechstabstandM && hoechstabstandM > 0))
        return unter.length;
    let anzahl = 1;
    let zuletzt = unter[0];
    let i = 0;
    while (zuletzt < unter[unter.length - 1] - 1e-9) {
        let weiteste = i + 1;
        for (let j = i + 1; j < unter.length && unter[j] - zuletzt <= hoechstabstandM + 1e-9; j++)
            weiteste = j;
        i = weiteste;
        zuletzt = unter[i];
        anzahl++;
    }
    return anzahl;
}
//# sourceMappingURL=tragwerk.js.map